/** @jsxImportSource @emotion/react */
import { useEffect, useState, useCallback, useMemo } from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { observer } from 'mobx-react-lite'
import { Link, useHistory } from 'react-router-dom'
import { useMobxStore } from 'src/state'
import { MENU_HEIGHT } from '../../css'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { ROUTES } from '@sportninja/common/constants/app'
import AccountSettings from 'src/components/AccountSettings'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import UpgradeAccountModal from 'src/components/ts-components/UpgradeAccountModal'
import Text from 'src/components/ts-components/Text'
import NotificationSettingsModal from 'src/components/NotificationSettingsModal'
import NotificationSettingsGearIcon from 'src/components/icons/NotificationSettingsGearIcon'
import UserSettingsIcon from 'src/components/icons/UserSettingsIcon'
import ProfileIcon from 'src/components/icons/ProfileIcon'
import AccountSettingsIcon from 'src/components/icons/AccountSettingsIcon'
import PrivacyPolicyIcon from 'src/components/icons/PrivacyPolicyIcon'
import LogoutIcon from 'src/components/icons/LogoutIcon'
import ProfileMenuItem from './components/ProfileMenuItem'

enum AccountType {
  FREE = 1,
  PROFESSIONAL = 2,
  ULTIMATE = 3,
}

const getAccountType = (id: number) => {
  switch (id) {
    case AccountType.PROFESSIONAL:
      return 'Professional'
    case AccountType.ULTIMATE:
      return 'Ultimate'
    default:
      return 'Free'
  }
}

const Separator = () => (
  <div
    css={css`
      width: 100%;
      height: 1px;
      background-color: ${colors.SOFT_STEEL};
    `}
  />
)

interface Props {
  onLogoutClick: () => void
  user: any
}

export const Profile = observer(({ onLogoutClick, user }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)
  const {
    me: {
      state: { me, account },
      clearUser,
      fetchMe,
    },
  } = useMobxStore()

  const history = useHistory()

  const isBusinessAccount = useMemo(() => {
    return me?.account_id && me?.account_type && me?.account_type > 1
  }, [me])

  const handleCreateLeagueAccount = useCallback(() => {
    setIsUpgradeModalOpen(true)
    setIsOpen(false) // Close the profile dropdown
  }, [])

  const confirmExit = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      clearUser()
      onLogoutClick()
    }
  }

  useEffect(() => {
    if (!me?.id) {
      fetchMe()
    }
  }, [me?.id, fetchMe])

  return (
    <>
      <div
        onClick={() => setIsOpen(!isOpen)}
        css={css`
          cursor: pointer;
          min-height: 34px;
          max-height: 34px;
          min-width: 34px;
          max-width: 34px;
          border-radius: 50%;
          display: flex;
          justify-content: center;
          align-items: center;
          overflow: hidden;
          border: 2px solid ${colors.ATTENDANCE_GRAY};
          transition: all 0.2s ease-in-out;

          &:hover {
            border: 2px solid ${colors.DEFAULT_FLAIR};
          }
        `}
      >
        {me?.image?.id ? (
          <div>
            <img
              alt={me?.name_first}
              src={getImageThumbnailId(me)}
              css={css`
                width: 100%;
                height: 100%;
              `}
            />
          </div>
        ) : (
          <div>
            <p
              css={css`
                font-size: 0.9rem;
                text-transform: uppercase;
              `}
            >
              {me?.name_first && me?.name_last
                ? me?.name_first[0] + me?.name_last[0]
                : '?'}
            </p>
          </div>
        )}
      </div>
      <div
        onClick={() => setIsOpen(false)}
        css={css`
          position: fixed;
          bottom: 0;
          right: 0;
          width: 100%;
          height: 100%;
          z-index: ${isOpen ? '1000' : '100'};
          pointer-events: ${isOpen ? 'all' : 'none'};
          transition: all 0.2s ease-in-out;

          display: ${isOpen ? 'flex' : 'none'};
        `}
      >
        <div
          css={css`
            position: absolute;
            top: ${MENU_HEIGHT + 8}px;
            right: 8px;
            z-index: ${isOpen ? '1001' : '101'};
            opacity: ${isOpen ? 1 : 0};
            display: ${isOpen ? 'flex' : 'none'};
            max-width: 275px;
            overflow: hidden;
            white-space: nowrap;
            transition: all 0.2s ease-in-out;
            background-color: ${colors.SECONDARY_300};
            padding: 16px;
            border-radius: 8px;
            flex-direction: column;
            gap: 16px;
            box-shadow: 0 4px 8px 1px rgba(0, 0, 0, 0.35);
            border: 1px solid ${colors.SOFT_STEEL};
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <div
              css={css`
                min-height: 40px;
                max-height: 40px;
                min-width: 40px;
                max-width: 40px;
                border-radius: 50%;
                display: flex;
                justify-content: center;
                align-items: center;
                overflow: hidden;
                border: 1px solid ${colors.NEUTRAL_0};
                background-color: ${colors.NEUTRAL_30};
              `}
            >
              {me?.image?.id ? (
                <div>
                  <img
                    alt={me?.name_first}
                    src={getImageThumbnailId(me)}
                    css={css`
                      width: 100%;
                      height: 100%;
                      object-fit: contain;
                    `}
                  />
                </div>
              ) : (
                <div>
                  <img
                    alt={me?.name_first}
                    src={'/images/generic_placeholders/placeholder_player.png'}
                    css={css`
                      width: 100%;
                      height: 100%;
                      object-fit: contain;
                    `}
                  />
                </div>
              )}
            </div>
            <div
              css={css`
                > p {
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  overflow: hidden;
                  max-width: 197px;
                }
              `}
            >
              <Text variant='heading6' weight='semiBold' color={colors.WHITE}>
                {`${user?.nameFirst}  ${user?.nameLast}`}
              </Text>
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                {user?.email}
              </Text>
            </div>
          </div>
          {isBusinessAccount && !isCanlan ? (
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                align-self: stretch;
                margin-bottom: -8px;
              `}
            >
              <Link
                to={ROUTES.GENERAL_SETTINGS + '?origin=subscription'}
                css={css`
                  display: flex;
                  padding: 4px 8px;
                  align-items: center;
                  gap: 8px;
                  border-radius: 4px;
                  background: ${colors.NEUTRAL_300};
                `}
              >
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_30}
                >
                  {getAccountType(Number(account?.subscription_type?.id) || 0)}
                </Text>
              </Link>
              {Number(account?.subscription_type?.id) === AccountType.FREE ? (
                <Link
                  to={ROUTES.GENERAL_SETTINGS + '?origin=subscription'}
                  css={css`
                    display: flex;
                    padding: 4px 8px;
                    align-items: center;
                    gap: 8px;
                    border-radius: 4px;
                    background: ${colors.PRIMARY};
                  `}
                >
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.SECONDARY_300}
                  >
                    Upgrade
                  </Text>
                </Link>
              ) : null}
            </div>
          ) : null}
          <Separator />
          <ProfileMenuItem
            icon={<ProfileIcon size={24} color={colors.PRIMARY} />}
            label='Profile'
            onClick={() => history.push(ROUTES.PROFILE.ROOT)}
          />
          <AccountSettings
            button={({ onClick }) => (
              <ProfileMenuItem
                icon={<UserSettingsIcon size={24} color={colors.PRIMARY} />}
                label='User Settings'
                onClick={onClick}
              />
            )}
          />
          <ProfileMenuItem
            icon={
              <NotificationSettingsGearIcon size={24} color={colors.PRIMARY} />
            }
            label='Notification Settings'
            onClick={() => setIsSettingsModalOpen(true)}
          />

          {(!me?.account_id || me?.account_type === 1) && !isCanlan ? (
            <>
              <Separator />
              <ProfileMenuItem
                icon={<AccountSettingsIcon size={24} color={colors.PRIMARY} />}
                label='Upgrade to League Account'
                onClick={handleCreateLeagueAccount}
                textColor={colors.PRIMARY}
              />
            </>
          ) : null}
          {me?.account_id && me?.account_type && me?.account_type > 1 ? (
            <>
              <Separator />
              <ProfileMenuItem
                icon={<AccountSettingsIcon size={24} color={colors.PRIMARY} />}
                label='Account Settings'
                onClick={() => history.push(ROUTES.GENERAL_SETTINGS)}
              />
            </>
          ) : null}
          <Separator />
          <a href='/docs/privacy.pdf' target='_blank' rel='noopener noreferrer'>
            <ProfileMenuItem
              icon={<PrivacyPolicyIcon size={24} color={colors.PRIMARY} />}
              label='Privacy Policy'
            />
          </a>
          <ProfileMenuItem
            icon={<LogoutIcon size={24} color={colors.ERROR_LIGHT} />}
            label='Log Out'
            onClick={confirmExit}
            textColor={colors.ERROR_LIGHT}
          />
        </div>
      </div>

      {/* Upgrade Account Modal */}
      <UpgradeAccountModal
        email={me?.email || ''}
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onComplete={() => {
          fetchMe()
        }}
        fetchMe={fetchMe}
      />

      {/* Notification Settings Modal */}
      <NotificationSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />
    </>
  )
})
